// src/pos-app/surfaces/kds/lib/boardFlow.js
//
// F12a-N Task K1 — the ticket-board engine behind the native KDS surface
// (visual contract: docs/mockups/f12a/kds-station.html; function contract:
// docs/superpowers/specs/2026-07-15-f12a-n-appendix-waiter-kds-inventory.md
// SURFACE B). Faithful, cut-and-paste extraction from
// pages/pos/OrderViewPage.js's timer/urgency/fetch-diff/completion logic,
// same discipline as surfaces/waiter/lib/drawerFlow.js and the cashier
// surface's lib/transactionFlow.js. The legacy page is UNTOUCHED and keeps
// its own inline copies of every function below — it still backs the web /
// Electron KDS route and KdsStation's SURFACES.kds='legacy' fallback. Only
// mechanical changes were made: closure-captured React state/refs became
// explicit parameters (`window.innerWidth` reads, `getTenantSetting`,
// `previousIds`, the audio ctx holder). No React imports here —
// params-for-closures only.
//
// Source-range table (legacy pages/pos/OrderViewPage.js, current line
// numbers):
//   DEFAULT_THRESHOLDS       <- 43      ({warning:10, critical:15} minutes)
//   normalizeUtc             <- 46-51   (GCP stores timestamps in UTC but
//                               may omit the Z suffix — appends 'Z' when no
//                               timezone marker so parseISO treats them as
//                               UTC. DELIBERATE exception to the project's
//                               local-time convention — inventory Surface B
//                               "Edge behaviors" flags it; do not "fix")
//   getElapsedMinutes        <- 53-59
//   getElapsedSeconds        <- 61-67   (floored at 0)
//   formatTimer              <- 69-73   (MM:SS, zero-padded)
//   getUrgency               <- 75-79   (fresh < warning <= critical)
//   getGridColumns           <- 88-94   (1 col under 768px; 3/4/5/6 by
//                               order count. window.innerWidth became the
//                               `windowWidth` param)
//   resolveThresholds        <- 561-565 (kds_timer_thresholds tenant
//                               setting, keyed PER CATEGORY, falling back
//                               to DEFAULT_THRESHOLDS. getTenantSetting
//                               became a param)
//   diffNewActiveIds         <- 610-621 (the pure ID-set diff inside
//                               fetchOrders: new-order detection compares
//                               fetched non-completed tracking_ids against
//                               the previous poll's set — works even when
//                               an order completes + a new one arrives in
//                               the same cycle. The setTimeout/sound side
//                               effects stay in the component)
//   emitOrderCompletedEvent  <- 710-720 (cross-tab signal after a commit:
//                               one CustomEvent + a localStorage set/remove
//                               pulse. KDS itself never listens — it polls,
//                               inventory Surface B "Polling / refresh" —
//                               but POS windows use it)
//   buildVoidsQuery          <- 647-653 (whole-sales-day window +
//                               void_type:'order_tracking' + page_size 100)
//   filterVoids              <- 654-658 (client-side filter: only voids for
//                               THIS KDS category AND the current session's
//                               sales_date)
//   getPrepMinutes           <- 101-106 (completed_at - submitted_at in
//                               whole minutes; null when either side is
//                               missing or unparseable. NOTE: legacy
//                               parseISO's the raw timestamps here WITHOUT
//                               normalizeUtc — kept byte-faithful, both
//                               sides carry the same offset so the delta is
//                               correct either way)
//   computeCompletedStats    <- 99-108  (count / avg prep '—' when empty /
//                               items prepared / over-critical-threshold
//                               count, exactly the CompletedTab strip)
//   playNotificationSound    <- 784-817 (3-tier playback: AudioContext
//                               800Hz sine w/ 1s exponential decay → HTML5
//                               Audio base64 WAV fallback → navigator.
//                               vibrate last resort. The component's
//                               `soundEnabled` state and `audioContextRef`
//                               became the `ctx` param {soundEnabled,
//                               audioRef}; the ref is both read and
//                               (re)assigned exactly like legacy)
import { parseISO } from 'date-fns';

// <- 43
export const DEFAULT_THRESHOLDS = { warning: 10, critical: 15 };

// <- 46-51
// GCP stores timestamps in UTC but may omit the Z suffix — normalize so parseISO treats them as UTC
export const normalizeUtc = (ts) => {
    if (!ts) return ts;
    if (/[Zz]$/.test(ts) || /[+-]\d{2}:\d{2}$/.test(ts)) return ts;
    return ts + 'Z';
};

// <- 53-59
export const getElapsedMinutes = (submittedAt) => {
    if (!submittedAt) return 0;
    try {
        const submitted = parseISO(normalizeUtc(submittedAt));
        return Math.floor((Date.now() - submitted.getTime()) / 60000);
    } catch { return 0; }
};

// <- 61-67
export const getElapsedSeconds = (submittedAt) => {
    if (!submittedAt) return 0;
    try {
        const submitted = parseISO(normalizeUtc(submittedAt));
        return Math.max(0, Math.floor((Date.now() - submitted.getTime()) / 1000));
    } catch { return 0; }
};

// <- 69-73
export const formatTimer = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// <- 75-79
export const getUrgency = (elapsedMinutes, thresholds) => {
    if (elapsedMinutes >= thresholds.critical) return 'critical';
    if (elapsedMinutes >= thresholds.warning) return 'warning';
    return 'fresh';
};

// The shortest viewport a wall-board layout is legible in.
//
// A ticket needs vertical room: header, item lines and a complete button.
// Below this there is only space for one ticket at a time, so a grid — however
// many columns — just shreds every card into its top strip.
const MIN_HEIGHT_FOR_COLUMNS = 500;

// Does this viewport get the swipeable carousel instead of a grid?
//
// Portrait phones only, and it is deliberately a WIDTH test. The carousel page
// is a column flex container, so its tickets shrink to fit the page height —
// fine in a portrait phone's ~700px, ruinous in a landscape phone's ~250px,
// where every card collapses to its OVERDUE strip.
//
// So a landscape phone does NOT come here. It is short but wide, and width is
// the thing it has: it takes the grid, which gives each card its natural
// height and scrolls vertically. See getGridColumns.
export const usesCarousel = (width) => width < 768;

// Is the viewport too short for a wall-board column count?
//
// Creamio, 2026-09-24: with the orientation lock removed the board finally
// rotated, but landscape still shredded every ticket — 852x393 cleared the
// 768px width test and took the wall-board layout built for a 1024px-tall
// tablet.
export const isShortViewport = (height) => height < MIN_HEIGHT_FOR_COLUMNS;

// <- 88-94 (window.innerWidth -> windowWidth param)
//
// `windowHeight` is optional and defaults to "tall enough", so callers that
// only know the width keep their existing behaviour.
//
// Why height matters at all (Creamio, 2026-09-22): kitchen staff opened the
// KDS on their phones and could neither read a ticket nor mark one complete —
// the shift was finished on a laptop instead. The station locked itself to
// landscape, which puts a phone at ~844px wide. That cleared the 768px
// breakpoint, so the board rendered a 3-column tablet layout into 390px of
// height and every card collapsed to an unreadable sliver.
//
// A portrait phone gets one column (and in practice the carousel instead).
//
// A LANDSCAPE phone gets four. It is short, so a tall wall-board column is
// wrong, but it is also 852px wide — room for four cards abreast, each at its
// natural height, scrolling vertically rather than swiping sideways. That is
// the shape the kitchen asked for after working a shift on one.
export const getGridColumns = (count, windowWidth, windowHeight = Infinity) => {
    if (windowWidth < 768) return 1;
    if (isShortViewport(windowHeight)) return 4;
    if (count <= 6) return 3;
    if (count <= 12) return 4;
    if (count <= 20) return 5;
    return 6;
};

// <- 561-565 (getTenantSetting -> param)
export const resolveThresholds = (getTenantSetting, category) => {
    const custom = getTenantSetting('kds_timer_thresholds', null);
    if (custom && custom[category]) return custom[category];
    return DEFAULT_THRESHOLDS;
};

// <- 610-621 (pure diff only; timers/sound stay in the component)
export const diffNewActiveIds = (fetchedOrders, previousIds) => {
    const activeIds = new Set(
        (fetchedOrders || []).filter(o => o.status !== 'completed').map(o => o.tracking_id)
    );
    const brandNew = [...activeIds].filter(id => !previousIds.has(id));
    return { activeIds, brandNew };
};

// <- 710-720
export const emitOrderCompletedEvent = (trackingId, category) => {
    const eventData = {
        type: 'orderStatusChanged',
        trackingId,
        category,
        oldStatus: 'submitted',
        newStatus: 'completed',
        timestamp: Date.now(),
    };
    window.dispatchEvent(new CustomEvent('orderEvent', { detail: eventData }));
    localStorage.setItem('orderEvent', JSON.stringify(eventData));
    localStorage.removeItem('orderEvent');
};

// <- 647-653
export const buildVoidsQuery = (activeSalesDate) => {
    const salesDay = activeSalesDate.split(' ')[0];
    return {
        void_type: 'order_tracking',
        start_date: salesDay + 'T00:00:00',
        end_date: salesDay + 'T23:59:59',
        page_size: 100,
    };
};

// <- 654-658
export const filterVoids = (items, category, activeSalesDate) => {
    return (items || []).filter(v => {
        const rd = v.record_data || {};
        return rd.category === category && rd.sales_date === activeSalesDate;
    });
};

// <- 101-106 (also 144-147, the same computation inline in the card body)
export const getPrepMinutes = (order) => {
    if (!order.submitted_at || !order.completed_at) return null;
    try {
        return Math.floor((parseISO(order.completed_at) - parseISO(order.submitted_at)) / 60000);
    } catch { return null; }
};

// <- 99-108
export const computeCompletedStats = (orders, thresholds) => {
    const completedCount = orders.length;
    const totalItems = orders.reduce((sum, o) => sum + (o.total_items_count || 0), 0);
    const prepTimes = orders.map(o => getPrepMinutes(o)).filter(t => t !== null);
    const avgPrep = prepTimes.length > 0
        ? (prepTimes.reduce((a, b) => a + b, 0) / prepTimes.length).toFixed(1)
        : '—';
    const overThreshold = prepTimes.filter(t => t >= thresholds.critical).length;
    return { completedCount, totalItems, avgPrep, overThreshold };
};

// <- 784-817 (soundEnabled state + audioContextRef -> ctx param)
export const playNotificationSound = async (ctx) => {
    const { soundEnabled, audioRef } = ctx;
    if (!soundEnabled) return;
    try {
        // Reuse warmed-up context or create new one
        let audioCtx = audioRef.current;
        if (!audioCtx || audioCtx.state === 'closed') {
            audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioRef.current = audioCtx;
        }
        if (audioCtx.state === 'suspended') await audioCtx.resume();

        const oscillator = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        oscillator.connect(gain);
        gain.connect(audioCtx.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gain.gain.setValueAtTime(0.8, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 1.0);
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + 1.0);
    } catch (err) {
        console.error('AudioContext sound error:', err);
        // Fallback: HTML5 Audio (works on Electron and most browsers)
        try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmBVDBxOqODowGQcBDaG0fHCdSk=');
            audio.volume = 0.8;
            await audio.play();
        } catch (fallbackErr) {
            // Last resort: vibration (mobile devices)
            if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        }
    }
};
